import type { IdentityDocumentSummary } from '../../../../../modules/identity/types';
import { getIdentityStatusConfig, isIdentityVerified } from '../../../../../modules/identity/helpers';

interface IdentityGatingBannerProps {
  summary: IdentityDocumentSummary | null | undefined;
  customerId: string;
  customerName: string;
  /** 若提供，表示 gateway / API 發生錯誤，無法確認狀態 */
  error?: string | null;
}

/**
 * 訂單詳情頁面的身分證件 gating banner
 *
 * - identityReady = summary.identityReady（優先使用 gateway 的 identity_ready）
 * - 若沒有 verified identity document：
 *   - 不可確認接單並產生合約
 *   - 不可產生合約
 *   - 顯示提示：「請先完成身分證件上傳與驗證」
 *   - 提供連結到該客戶的證件管理區
 */
export function IdentityGatingBanner({
  summary,
  customerId,
  customerName,
  error,
}: IdentityGatingBannerProps): JSX.Element {
  // 若 API 失敗或無法確認狀態，顯示 amber 提示
  if (error || !summary || summary.source === 'unknown') {
    return (
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
            <i className="ri-question-line text-lg text-amber-600"></i>
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-amber-800">身分驗證狀態暫時無法確認</h4>
            <p className="text-sm text-amber-700 mt-1">
              無法連線到身分驗證服務，請稍後再試。訂單處理可能受到限制。
            </p>
          </div>
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
            <i className="ri-question-line"></i>
            未知
          </span>
        </div>
      </div>
    );
  }

  const verified = isIdentityVerified(summary);
  const config = getIdentityStatusConfig(summary?.overallStatus);

  if (verified) {
    return (
      <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-emerald-100 rounded-full flex-shrink-0">
            <i className="ri-shield-check-line text-lg text-emerald-600"></i>
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-emerald-800">身分驗證已完成</h4>
            <p className="text-sm text-emerald-700 mt-1">
              客戶「{customerName}」的身分證件已通過核驗，可進行合約簽署。
            </p>
          </div>
          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
            <i className={config.icon}></i>
            {config.label}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
          <i className="ri-error-warning-line text-lg text-red-600"></i>
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-red-800">尚未完成身分驗證</h4>
          <p className="text-sm text-red-700 mt-1">
            客戶「{customerName}」尚未上傳或完成身分證件核驗。根據 LensBank 政策，
            <strong>未完成身分驗證的訂單不可確認接單或產生合約</strong>。
          </p>
          <div className="mt-3 flex items-center gap-3">
            <a
              href={`/admin/customers`}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-user-settings-line"></i>
              前往客戶管理
            </a>
            <span className="text-xs text-red-600">
              客戶 ID：{customerId}
            </span>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
          <i className={config.icon}></i>
          {config.label}
        </span>
      </div>
    </div>
  );
}